import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";

/* ─────────────────────────────────────────────────────────────
   BLOG ILLUSTRATION
───────────────────────────────────────────────────────────── */
const BlogHeroIllustration = ({ color = "#0d6efd", title = "" }) => {
  const initials = title.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const uid = `hero${color.replace("#", "")}`;
  return (
    <svg viewBox="0 0 900 400" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <linearGradient id={uid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.14" />
          <stop offset="100%" stopColor={color} stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <rect width="900" height="400" fill={`url(#${uid})`} />
      <circle cx="800" cy="60" r="150" fill={color} fillOpacity="0.07" />
      <circle cx="100" cy="350" r="120" fill={color} fillOpacity="0.05" />
      <circle cx="450" cy="200" r="60" fill={color} fillOpacity="0.1" />
      <text x="450" y="212" textAnchor="middle" fontSize="40" fontWeight="800" fill={color} fillOpacity="0.6" fontFamily="sans-serif">{initials}</text>
      <rect x="200" y="140" width="500" height="5" rx="3" fill={color} fillOpacity="0.15" />
      <rect x="250" y="160" width="400" height="5" rx="3" fill={color} fillOpacity="0.1" />
      <rect x="300" y="180" width="300" height="5" rx="3" fill={color} fillOpacity="0.07" />
    </svg>
  );
};

const SmallIllustration = ({ color = "#0d6efd", title = "" }) => {
  const initials = title.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const uid = `sm${color.replace("#", "")}${initials}`;
  return (
    <svg viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <linearGradient id={uid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect width="120" height="90" fill={`url(#${uid})`} />
      <circle cx="100" cy="15" r="30" fill={color} fillOpacity="0.08" />
      <circle cx="60" cy="45" r="16" fill={color} fillOpacity="0.12" />
      <text x="60" y="51" textAnchor="middle" fontSize="11" fontWeight="bold" fill={color} fillOpacity="0.7" fontFamily="sans-serif">{initials}</text>
    </svg>
  );
};

const RELATED_COLORS = ["#0d6efd", "#198754", "#dc3545", "#fd7e14", "#6f42c1"];

const resolveMediaUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return `${API_BASE_URL}${value.replace(/^\/+/, "")}`;
};

const getVideoEmbedUrl = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const BlogDetail = () => {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const [copied, setCopied]   = useState(false);
  const [blog, setBlog]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMediaTab, setActiveMediaTab] = useState("gallery");

  const token = localStorage.getItem("authToken");

  useEffect(() => { fetchBlogDetail(); }, [slug]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [slug]);

  const fetchBlogDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/blog_detail/${slug}`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.status) {
        const item = data.data;

        const formattedBlog = {
          id:                 item.id,
          slug:               item.slug,
          title:              item.name,
          excerpt:            item.short_desc,
          content:            item.content,
          category_name:      item.category,
          category_id:        item.category,
          // ✅ Use author_name from API; fallback to "Admin"
          author_name:        item.author_name || "Admin",
          author_designation: "Medical Expert",
          author_bio:         "",
          published_at:       item.created_at,
          read_time:          5,
          views:              item.views || 0,
          tags:               item.keywords ? item.keywords.split(",").map(t => t.trim()).filter(Boolean) : [],
          color:              "#1E7A8C",
          image:              item.blogimg
            ? `${API_BASE_URL}public/blog/${item.blogimg}`
            : null,
          gallery:           Array.isArray(item.gallery) ? item.gallery : Array.isArray(item.photos) ? item.photos : [],
          videos:            Array.isArray(item.videos) ? item.videos : [],
        };

        setBlog(formattedBlog);

        // ✅ Use related_blog_objects returned by API (from admin-selected related blogs)
        if (item.related_blog_objects && item.related_blog_objects.length > 0) {
          const formattedRelated = item.related_blog_objects.map((r, index) => ({
            id:           r.id,
            slug:         r.slug,
            title:        r.name,
            excerpt:      r.short_desc,
            category_name: r.category,
            author_name:  r.author_name || "Admin",
            published_at: r.created_at,
            color:        RELATED_COLORS[index % RELATED_COLORS.length],
            image:        r.blogimg
              ? `${API_BASE_URL}public/blog/${r.blogimg}`
              : null,
          }));
          setRelated(formattedRelated);
        } else {
          // Fallback: fetch by same category if no related blogs are set
          fetchRelatedByCategory(item.category, item.id);
        }
      }
    } catch (error) {
      console.error("Blog detail fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* Fallback: fetch by category if no related_blog_objects */
  const fetchRelatedByCategory = async (category, currentId) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/blogs_front`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.status) {
        const formattedRelated = data.data
          .filter((item) => item.category === category && item.id !== currentId)
          .slice(0, 3)
          .map((item, index) => ({
            id:           item.id,
            slug:         item.slug,
            title:        item.name,
            excerpt:      item.short_desc,
            category_name: item.category,
            author_name:  item.author_name || "Admin",
            published_at: item.created_at,
            color:        RELATED_COLORS[index % RELATED_COLORS.length],
            image:        item.blogimg
              ? `${API_BASE_URL}public/blog/${item.blogimg}`
              : null,
          }));
        setRelated(formattedRelated);
      }
    } catch (error) {
      console.error("Related blogs fetch error:", error);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const galleryItems = useMemo(() => {
    const raw = Array.isArray(blog?.gallery) ? blog.gallery : Array.isArray(blog?.photos) ? blog.photos : [];
    return raw
      .map((item, index) => {
        if (typeof item === "string") {
          return { id: `gallery-${index}`, url: item, title: "" };
        }
        if (item && typeof item === "object") {
          return {
            id: item.id || `gallery-${index}`,
            url: item.url || item.image || item.img || "",
            title: item.caption || item.title || "",
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [blog]);

  const videoItems = useMemo(() => {
    const raw = Array.isArray(blog?.videos) ? blog.videos : [];
    return raw
      .map((item, index) => {
        if (typeof item === "string") {
          return { id: `video-${index}`, url: item, title: "" };
        }
        if (item && typeof item === "object") {
          return {
            id: item.id || `video-${index}`,
            url: item.url || item.link || item.video || "",
            title: item.title || item.caption || "",
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [blog]);

  /* Loading */
  if (loading) {
    return (
      <>
        <Header />
        <div style={{ textAlign: "center", padding: "80px 32px" }}>
          <div style={{ width: 44, height: 44, border: "3px solid #d6efe2", borderTopColor: "#1E7A8C", borderRadius: "50%", animation: "spin .8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#6b7280", fontSize: 14 }}>Loading article…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <Footer />
      </>
    );
  }

  /* 404 */
  if (!blog) {
    return (
      <div className="bd-page">
        <style>{PAGE_STYLES}</style>
        <Header />
        <div className="bd-notfound">
          <div className="bd-nf-icon"><i className="fa-solid fa-file-circle-xmark" /></div>
          <h3>Article Not Found</h3>
          <p>This article may have been removed or the link is incorrect.</p>
          <button className="bd-back-btn" onClick={() => navigate("/blog")}>
            <i className="fa-solid fa-arrow-left" /> Back to Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bd-page">
      <style>{PAGE_STYLES}</style>
      <Header />

      {/* Breadcrumb */}
      <section className="bd-bc">
        <div className="container-fluid px-lg-5">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
              <li className="breadcrumb-item active" aria-current="page">
                {blog.title.length > 55 ? blog.title.substring(0, 55) + "…" : blog.title}
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Article */}
      <section className="bd-section">
        <div className="container-fluid px-lg-5">
          <div className="bd-layout">

            {/* ── Content ── */}
            <article className="bd-content">

              {/* Hero Image */}
              <div className="bd-hero">
                {blog.image ? (
                  <img src={blog.image} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <BlogHeroIllustration color={blog.color} title={blog.title} />
                )}
                <span className="bd-hero-badge" style={{ background: blog.color }}>{blog.category_name}</span>
              </div>

              {/* Header */}
              <div className="bd-art-header">
                <div className="bd-meta-row">
                  <span className="bd-mi">
                    <i className="fa-regular fa-calendar" />
                    {new Date(blog.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </span>
                  <span className="bd-mi"><i className="fa-regular fa-clock" />{blog.read_time} min read</span>
                  <span className="bd-mi"><i className="fa-regular fa-eye" />{blog.views.toLocaleString()} views</span>
                </div>

                <h1 className="bd-title">{blog.title}</h1>

                <div className="bd-author-bar">
                  {/* ✅ Author avatar uses first letter of author_name from API */}
                  <div className="bd-av" style={{ background: blog.color }}>
                    {(blog.author_name || "A").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="bd-an">{blog.author_name}</div>
                    <div className="bd-ar">{blog.author_designation}</div>
                  </div>
                  <div className="bd-share ms-auto">
                    <span className="bd-share-lbl">Share:</span>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="bd-sb bd-fb" title="Facebook"><i className="fa-brands fa-facebook-f" /></a>
                    <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`} target="_blank" rel="noreferrer" className="bd-sb bd-tw" title="Twitter"><i className="fa-brands fa-x-twitter" /></a>
                    <a href={`https://wa.me/?text=${encodeURIComponent(blog.title + " " + window.location.href)}`} target="_blank" rel="noreferrer" className="bd-sb bd-wa" title="WhatsApp"><i className="fa-brands fa-whatsapp" /></a>
                    <button className={`bd-sb bd-cp${copied ? " bd-copied" : ""}`} onClick={handleCopy} title="Copy link">
                      <i className={`fa-solid ${copied ? "fa-check" : "fa-link"}`} />
                    </button>
                  </div>
                </div>
              </div>

              <hr className="bd-hr" />

              {/* Body */}
              <div className="bd-body" dangerouslySetInnerHTML={{ __html: blog.content }} />

              {(galleryItems.length > 0 || videoItems.length > 0) && (
                <section className="bd-media-section">
                  <div className="bd-media-tabs">
                    <button
                      type="button"
                      className={`bd-media-btn ${activeMediaTab === "gallery" ? "active" : ""}`}
                      onClick={() => setActiveMediaTab("gallery")}
                    >
                      <i className="fa-solid fa-images" /> Gallery
                      {galleryItems.length > 0 && <span className="bd-media-count">{galleryItems.length}</span>}
                    </button>
                    <button
                      type="button"
                      className={`bd-media-btn ${activeMediaTab === "videos" ? "active" : ""}`}
                      onClick={() => setActiveMediaTab("videos")}
                    >
                      <i className="fa-solid fa-video" /> Videos
                      {videoItems.length > 0 && <span className="bd-media-count">{videoItems.length}</span>}
                    </button>
                  </div>

                  <div className="bd-media-panel">
                    {activeMediaTab === "gallery" ? (
                      galleryItems.length > 0 ? (
                        <div className="bd-gallery-grid">
                          {galleryItems.map((item) => (
                            <figure className="bd-gallery-card" key={item.id}>
                              <img src={resolveMediaUrl(item.url)} alt={item.title || "Blog gallery image"} />
                              {item.title && <figcaption className="bd-gallery-caption">{item.title}</figcaption>}
                            </figure>
                          ))}
                        </div>
                      ) : (
                        <div className="bd-empty">No gallery images are available for this article yet.</div>
                      )
                    ) : videoItems.length > 0 ? (
                      <div className="bd-video-grid">
                        {videoItems.map((item) => {
                          const embed = getVideoEmbedUrl(item.url);
                          return (
                            <figure className="bd-video-card" key={item.id}>
                              {embed ? (
                                <iframe
                                  className="bd-video-frame"
                                  src={embed}
                                  title={item.title || "Blog video"}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              ) : (
                                <video className="bd-video-frame" src={resolveMediaUrl(item.url)} controls />
                              )}
                              {item.title && <figcaption className="bd-video-caption">{item.title}</figcaption>}
                            </figure>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="bd-empty">No videos are available for this article yet.</div>
                    )}
                  </div>
                </section>
              )}

              {/* Tags */}
              {blog.tags.length > 0 && (
                <div className="bd-tags">
                  <span className="bd-tags-lbl"><i className="fa-solid fa-tags" /> Tags:</span>
                  {blog.tags.map((tag, i) => <span key={i} className="bd-tag">{tag}</span>)}
                </div>
              )}

              {/* Back */}
              <div className="bd-back-row">
                <button className="bd-back-btn" onClick={() => navigate("/blog")}>
                  <i className="fa-solid fa-arrow-left" /> Back to Blog
                </button>
              </div>
            </article>

            {/* ── Sidebar ── */}
            <aside className="bd-sidebar">

              {/* Author card */}
              <div className="bd-sc">
                <div className="bd-sch"><i className="fa-solid fa-circle-info" /> About the Author</div>
                <div className="bd-sc-body text-center" style={{ padding: "20px 18px" }}>
                  <div className="bd-av-lg" >
                    {(blog.author_name || "A").charAt(0).toUpperCase()}
                  </div>
                  <div className="bd-an-lg">{blog.author_name}</div>
                  <div className="bd-ar-sm">{blog.author_designation}</div>
                  {blog.author_bio && <p className="bd-bio">{blog.author_bio}</p>}
                </div>
              </div>

              {/* ✅ Related blogs from API (related_blog_objects or fallback by category) */}
              {related.length > 0 && (
                <div className="bd-sc">
                  <div className="bd-sch"><i className="fa-solid fa-newspaper" /> Related Articles</div>
                  <div className="bd-rl">
                    {related.map((post, index) => (
                      <Link to={`/blog/${post.slug}`} key={post.id} className="bd-ri">
                        <div className="bd-ri-img">
                          {post.image ? (
                            <img src={post.image} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <SmallIllustration color={post.color} title={post.title} />
                          )}
                        </div>
                        <div className="bd-ri-body">
                          <div className="bd-ri-title">{post.title}</div>
                          <div className="bd-ri-meta">
                            {post.author_name && (
                              <span className="bd-ri-author">
                                <i className="fa-solid fa-user" />
                                {post.author_name}
                              </span>
                            )}
                            <div className="bd-ri-date">
                              <i className="fa-regular fa-calendar" />
                              {new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA — Clinics */}
              <div className="bd-sc bd-cta">
                <div className="bd-cta-icon"><i className="fa-solid fa-hospital" /></div>
                <div className="bd-cta-title">Find a Clinic Near You</div>
                <p className="bd-cta-text">Browse verified clinics and book your appointment today.</p>
                <Link to="/clinic-list-page" className="bd-cta-btn"><i className="fa-solid fa-search" /> Find Clinics</Link>
              </div>

              {/* CTA — Doctors */}
              <div className="bd-sc bd-cta">
                <div className="bd-cta-icon"><i className="fa-solid fa-user-doctor" /></div>
                <div className="bd-cta-title">Find a Doctor Near You</div>
                <p className="bd-cta-text">Browse verified doctors and book your appointment today.</p>
                <Link to="/doctor-list-page" className="bd-cta-btn"><i className="fa-solid fa-search" /> Find Doctor</Link>
              </div>

            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────────── */
const PAGE_STYLES = `
  :root{
    --p:var(--grad-main);--pd:#175f6d;--pl:#e8f5f7;--pb:#b2dde4;
    --tx:#212529;--mu:#6c757d;
    --bo:#dee2e6;--bg:#f4f9fa;--wh:#ffffff;
    --r:12px;--sh:0 2px 16px rgba(30,122,140,0.08);
    --fh:'Inter',sans-serif;--fb:'Poppins',sans-serif;
  }
  .bd-page{font-family:var(--fb);background:var(--bg);min-height:100vh;}

  /* breadcrumb */
  .bd-bc{background:linear-gradient(135deg,#e8f5f7,#f0fafb);border-bottom:1px solid #b2dde4;padding:13px 0;}
  .bd-bc .breadcrumb{margin:0;}
  .bd-bc .breadcrumb-item a{color:var(--p);text-decoration:none;font-size:14px;}
  .bd-bc .breadcrumb-item.active{color:var(--mu);font-size:14px;}

  /* section + layout */
  .bd-section{padding:36px 0 56px;}
  .bd-layout{display:grid;grid-template-columns:1fr 300px;gap:28px;align-items:start;}
  @media(max-width:991px){.bd-layout{grid-template-columns:1fr;}.bd-sidebar{order:-1;}}

  /* hero */
  .bd-hero{position:relative;border-radius:var(--r);overflow:hidden;aspect-ratio:16/6;margin-bottom:28px;box-shadow:var(--sh);background:#e8f5f7;}
  .bd-hero svg{display:block;width:100%;height:100%;}
  .bd-hero-badge{position:absolute;top:16px;left:16px;color:#fff;font-size:12px;font-weight:700;padding:4px 12px;border-radius:20px;font-family:var(--fh);letter-spacing:.4px;}

  /* article header */
  .bd-art-header{margin-bottom:4px;}
  .bd-meta-row{display:flex;flex-wrap:wrap;gap:16px;margin-bottom:14px;}
  .bd-mi{display:flex;align-items:center;gap:5px;font-size:13px;color:var(--mu);}
  .bd-mi i{font-size:12px;color:var(--p);}
  .bd-title{font-family:var(--fh);font-size:clamp(22px,3vw,32px);font-weight:800;color:var(--tx);line-height:1.25;margin:0 0 20px;letter-spacing:-.3px;}

  /* author bar */
  .bd-author-bar{display:flex;align-items:center;gap:12px;background:var(--pl);border:1px solid var(--pb);border-radius:10px;padding:12px 16px;flex-wrap:wrap;}
  .bd-av{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;flex-shrink:0;font-family:var(--fh);}
  .bd-an{font-size:14px;font-weight:700;color:var(--tx);font-family:var(--fh);}
  .bd-ar{font-size:12px;color:var(--mu);}

  /* share */
  .bd-share{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
  .bd-share-lbl{font-size:13px;color:var(--mu);margin-right:2px;}
  .bd-sb{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;color:#fff;border:none;cursor:pointer;transition:transform .2s,opacity .2s;text-decoration:none;}
  .bd-sb:hover{transform:translateY(-2px);opacity:.88;}
  .bd-fb{background:#1877f2;}.bd-tw{background:#000;}.bd-wa{background:#25d366;}.bd-cp{background:var(--mu);}
  .bd-copied{background:var(--p) !important;}

  /* divider */
  .bd-hr{border:none;border-top:2px solid var(--bo);margin:24px 0;}

  /* article body */
  .bd-body{font-size:16px;line-height:1.85;color:#343a40;animation:bdIn .5s ease both;}
  @keyframes bdIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
  .bd-body h2,.bd-body h3,.bd-body h4{font-family:var(--fh);font-weight:700;color:var(--tx);margin:28px 0 12px;}
  .bd-body h2{font-size:22px;}.bd-body h3{font-size:18px;}.bd-body h4{font-size:16px;}
  .bd-body p{margin-bottom:16px;}
  .bd-body a{color:var(--p);text-decoration:underline;text-underline-offset:3px;}
  .bd-body img{max-width:100%;border-radius:8px;margin:16px 0;box-shadow:var(--sh);}
  .bd-body ul,.bd-body ol{padding-left:24px;margin-bottom:16px;}
  .bd-body li{margin-bottom:6px;}
  .bd-body blockquote{border-left:4px solid var(--p);background:var(--pl);padding:14px 20px;border-radius:0 8px 8px 0;margin:20px 0;font-style:italic;color:#495057;font-size:15.5px;}
  .bd-body pre{background:#f1f3f5;border-radius:6px;padding:16px;overflow-x:auto;margin:16px 0;font-size:14px;}
  .bd-body code{background:#f1f3f5;border-radius:4px;padding:2px 6px;font-size:14px;}

  /* media tabs */
  .bd-media-section{margin-top:30px;padding:18px;border:1px solid var(--bo);border-radius:var(--r);background:var(--wh);box-shadow:var(--sh);}
  .bd-media-tabs{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px;}
  .bd-media-btn{display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border:1px solid var(--pb);border-radius:999px;background:var(--pl);color:var(--p);font-size:13px;font-weight:700;cursor:pointer;transition:all .2s ease;}
  .bd-media-btn.active{background:var(--p);color:#fff;border-color:var(--p);}
  .bd-media-count{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 7px;border-radius:999px;background:rgba(255,255,255,0.22);font-size:11px;font-weight:700;}
  .bd-media-panel{display:block;}
  .bd-gallery-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;}
  .bd-gallery-card{overflow:hidden;border:1px solid var(--bo);border-radius:10px;background:#f8fbfc;}
  .bd-gallery-card img{display:block;width:100%;aspect-ratio:4/3;object-fit:cover;}
  .bd-gallery-caption,.bd-video-caption{padding:10px 12px;font-size:13px;color:var(--mu);line-height:1.45;}
  .bd-video-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;}
  .bd-video-card{overflow:hidden;border:1px solid var(--bo);border-radius:10px;background:#f8fbfc;}
  .bd-video-frame{display:block;width:100%;aspect-ratio:16/9;border:0;background:#000;}
  .bd-empty{padding:14px 16px;border:1px dashed var(--pb);border-radius:10px;color:var(--mu);background:var(--pl);font-size:14px;}

  /* tags */
  .bd-tags{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:32px 0 0;padding:20px 0;border-top:1px solid var(--bo);}
  .bd-tags-lbl{font-size:13px;font-weight:600;color:var(--mu);margin-right:4px;}
  .bd-tag{display:inline-flex;align-items:center;padding:4px 12px;border:1px solid var(--pb);border-radius:20px;font-size:12.5px;color:var(--p);background:var(--pl);font-weight:500;}
  .bd-tag:hover{background:#c8ecf1;border-color:var(--p);}

  /* back */
  .bd-back-row{margin-top:28px;}
  .bd-back-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 20px;border:1.5px solid var(--p);border-radius:50px;color:var(--p);font-size:14px;font-weight:600;font-family:var(--fh);background:none;cursor:pointer;text-decoration:none;transition:background .2s,color .2s;}
  .bd-back-btn:hover{background:var(--p);color:#fff;}

  /* sidebar */
  .bd-sidebar{display:flex;flex-direction:column;gap:20px;}
  .bd-sc{background:var(--wh);border:1px solid var(--bo);border-radius:var(--r);overflow:hidden;box-shadow:var(--sh);}
  .bd-sch{display:flex;align-items:center;gap:8px;padding:14px 18px;background:var(--pl);border-bottom:1px solid var(--pb);font-family:var(--fh);font-size:14px;font-weight:700;color:var(--tx);}
  .bd-sch i{color:var(--p);}
  .bd-av-lg{width:60px;height:60px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:800;color:#fff;margin:0 auto 12px;background:var(--grad-main);}
  .bd-an-lg{font-family:var(--fh);font-size:15px;font-weight:700;color:var(--tx);}
  .bd-ar-sm{font-size:12.5px;color:var(--mu);margin-top:2px;}
  .bd-bio{font-size:13px;color:var(--mu);margin-top:10px;line-height:1.6;}

  /* related */
  .bd-rl{padding:12px;display:flex;flex-direction:column;gap:4px;}
  .bd-ri{display:flex;gap:12px;padding:10px 8px;border-radius:8px;text-decoration:none;transition:background .15s;}
  .bd-ri:hover{background:var(--pl);}
  .bd-ri-img{width:64px;height:56px;border-radius:8px;overflow:hidden;flex-shrink:0;background:#e9ecef;}
  .bd-ri-img svg{display:block;width:100%;height:100%;}
  .bd-ri-img img{display:block;width:100%;height:100%;object-fit:cover;}
  .bd-ri-body{flex:1;min-width:0;}
  .bd-ri-title{font-size:13px;font-weight:600;color:var(--tx);font-family:var(--fh);line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin-bottom:4px;}
  .bd-ri-meta{display:flex;flex-direction:column;gap:3px;}
  .bd-ri-author{display:flex;align-items:center;gap:4px;font-size:11px;color:var(--grad-main);font-weight:600;}
  .bd-ri-author i{font-size:9px;}
  .bd-ri-date{display:flex;align-items:center;gap:4px;font-size:11.5px;color:var(--mu);}
  .bd-ri-date i{font-size:10px;color:var(--p);}

  /* cta */
  .bd-cta{padding:24px 20px;text-align:center;background:linear-gradient(135deg,var(--pl),#f0fafb);}
  .bd-cta-icon{width:52px;height:52px;background:var(--grad-main);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;color:#fff;margin:0 auto 14px;}
  .bd-cta-title{font-family:var(--fh);font-size:15px;font-weight:800;color:var(--tx);margin-bottom:8px;}
  .bd-cta-text{font-size:13px;color:var(--mu);margin-bottom:16px;line-height:1.5;}
  .bd-cta-btn{display:inline-flex;align-items:center;gap:6px;padding:10px 20px;background:var(--p);color:#fff;border-radius:50px;font-size:14px;font-weight:700;font-family:var(--fh);text-decoration:none;transition:background .2s,transform .15s;}
  .bd-cta-btn:hover{background:var(--pd);transform:translateY(-1px);color:#fff;}

  /* not found */
  .bd-notfound{text-align:center;padding:80px 32px;max-width:480px;margin:0 auto;}
  .bd-nf-icon{width:80px;height:80px;background:#fee2e2;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:32px;color:#ef4444;margin:0 auto 20px;}
  .bd-notfound h3{font-family:var(--fh);font-weight:800;color:var(--tx);margin-bottom:10px;}
  .bd-notfound p{font-size:14px;color:var(--mu);margin-bottom:24px;}
`;

export default BlogDetail;